import { useCallback, useEffect, useState } from "react";
import { Mail, Inbox } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STATUS_STYLES = {
  new: "bg-[#D4AF37]/15 text-[#D4AF37]",
  open: "bg-blue-500/15 text-blue-400",
  resolved: "bg-emerald-500/15 text-emerald-400",
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

export default function AdminTickets() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const load = useCallback(() => api.get("/admin/tickets").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const update = async (id, payload) => {
    try {
      const { data: t } = await api.patch(`/admin/tickets/${id}`, payload);
      toast.success("Ticket updated"); setActive(t); setReply(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const counts = data?.counts || {};
  return (
    <div data-testid="admin-tickets">
      <div className="flex gap-3 mb-5">
        {["new", "open", "resolved"].map((s) => (
          <div key={s} className="rounded-xl border border-white/8 bg-[#121214] px-4 py-2.5">
            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[s]}`}>{s}</span>
            <span className="ml-2 text-white font-medium">{counts[s] ?? 0}</span>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr,1.2fr] gap-4">
        <div className="rounded-2xl border border-white/8 bg-[#121214] overflow-hidden max-h-[560px] overflow-y-auto" data-testid="admin-tickets-list">
          {(data?.tickets || []).length === 0 ? (
            <div className="p-10 text-center text-[#71717A]"><Inbox className="w-8 h-8 mx-auto mb-2" /> No tickets.</div>
          ) : data.tickets.map((t) => (
            <button key={t.contact_id} onClick={() => { setActive(t); setReply(t.reply || ""); }} data-testid={`admin-ticket-${t.contact_id}`}
              className={`w-full text-left px-5 py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02] ${active?.contact_id === t.contact_id ? "bg-white/[0.03]" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-white text-sm font-medium truncate">{t.subject}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full capitalize shrink-0 ${STATUS_STYLES[t.status || "new"]}`}>{t.status || "new"}</span>
              </div>
              <div className="text-xs text-[#71717A] mt-1">{t.name} · {t.email}</div>
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-white/8 bg-[#121214] p-6" data-testid="admin-ticket-detail">
          {!active ? (
            <div className="text-center text-[#71717A] py-20"><Mail className="w-8 h-8 mx-auto mb-2" /> Select a ticket to view.</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-1"><h3 className="text-white font-medium">{active.subject}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[active.status || "new"]}`}>{active.status || "new"}</span></div>
              <div className="text-xs text-[#71717A] mb-4">{active.name} · {active.email} · {fmtDate(active.created_at)}</div>
              <p className="text-sm text-[#D4D4D8] whitespace-pre-wrap bg-[#0d0d0f] rounded-xl p-4 border border-white/8 mb-4">{active.message}</p>
              <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Internal reply / notes…" data-testid="admin-ticket-reply" className="bg-[#0d0d0f] border-white/10 text-white min-h-[90px] mb-3" />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => update(active.contact_id, { status: "open", reply })} data-testid="admin-ticket-open" className="bg-white/5 border border-white/15 text-white rounded-full hover:bg-white/10">Mark open & save</Button>
                <Button size="sm" onClick={() => update(active.contact_id, { status: "resolved", reply })} data-testid="admin-ticket-resolve" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">Resolve</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
